// =====================================================================
// crmCustomer — Forecast category editor (P2.1)
//
// ForecastEditor — change the forecast category. When the new category
// requires a higher MEDDPICCR floor than the deal's current score, the
// editor demands a free-text note and persists it (P2.1). The banner
// stays advisory — the spec is explicit there is no server gate.
//
// Used inline on the Overview tab of OppDetail.
// =====================================================================
function ForecastEditor({ opp, derivedScoreGot, onChange }) {
  const auth = useAuth();
  const userId = (auth && auth.user && (auth.user.id || auth.user.email)) || "";
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(opp.forecastCategory || "Pipeline");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setCategory(opp.forecastCategory || "Pipeline");
    setNote("");
    setError(null);
  }, [opp.id, opp.forecastCategory, open]);

  const stageMin = window.tpThresholds.stageForecastMin(D.SETTINGS);
  const requiredFloor = stageMin[category] || 0;
  const noteRequired = derivedScoreGot < requiredFloor;
  const categories = (D.FORECAST_CATEGORIES || ["Pipeline", "Best case", "Commit", "Closed"]);
  const fcSet = (window.D && D.FORECAST_CATEGORIES) || categories;

  async function save() {
    setSaving(true); setError(null);
    try {
      if (noteRequired) {
        if (!note.trim()) {
          setError("This category requires a higher MEDDPICCR score (" + requiredFloor + ") than this deal has (" + derivedScoreGot + "). Add a note explaining why you're forecasting it anyway.");
          setSaving(false);
          return;
        }
        if (!userId) {
          setError("Cannot record a forecast override without a signed-in user.");
          setSaving(false);
          return;
        }
      }
      const payload = {
        forecastCategory: category,
        forecastChanged: new Date().toISOString().slice(0, 10),
      };
      if (noteRequired) {
        payload.forecastOverride = {
          note: note.trim(), by: userId, at: new Date().toISOString(),
        };
      } else {
        // Clearing — moving back into bounds means the note no longer applies.
        payload.forecastOverride = null;
      }
      const updated = await tpAPI.opportunities.update(opp.id, payload);
      onChange && onChange(updated);
      setOpen(false);
    } catch (e) {
      setError(e.message || "Failed to save forecast.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button className="btn btn-sm" onClick={() => setOpen(true)}>Change forecast</button>
      {open && (
        <Dialog
          title="Forecast category"
          onClose={() => setOpen(false)}
          footer={
            <>
              <button className="btn btn-sm" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          }
        >
          <div className="form-field">
            <label>Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}>
              {(fcSet || []).map(c => <option key={c}>{c}</option>)}
            </select>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Floor for <strong>{category}</strong>: {requiredFloor} of 9 MEDDPICCR elements. This deal has {derivedScoreGot}.
            </div>
          </div>
          {noteRequired && (
            <div className="form-field">
              <label>Override note (required — below threshold)</label>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                placeholder="Why is this deal worth a higher forecast despite the score? The note lives in the deal's audit trail."
                style={{ width: "100%", fontFamily: "inherit", fontSize: 13 }} />
            </div>
          )}
          {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
        </Dialog>
      )}
    </>
  );
}

window.ForecastEditor = ForecastEditor;
